/**
 * titleCase.js — Client Intake auto-capitalise helper.
 * ────────────────────────────────────────────────────
 * v10.28 (Feb 2026 · Batch B · Issue 5a)
 *
 * Rules (locked with Trevor for go-live):
 *   1. If the input already contains ANY uppercase letter, return it
 *      unchanged. Preserves operator intent for names the algorithm
 *      would butcher (McDonald, iPhone, DHL, etc.).
 *   2. Otherwise Title Case each word AND capitalise:
 *      - the letter after a Mc prefix   (mcdonald → McDonald)
 *      - the letter after a Mac prefix  (macgregor → MacGregor)  ONLY
 *        when the word is 5+ chars long, so "Mace" / "Mack" don't
 *        become "MacE" / "MacK".
 *      - the letter after an apostrophe (o'connor → O'Connor)
 *      - the letter after a hyphen      (smith-jones → Smith-Jones)
 *
 * Non-alpha content (digits, punctuation) is preserved as-is.
 * Fires on blur — never on keystroke — so mid-typing state is stable.
 */

const MAC_MIN_LEN = 5;

function _capitaliseWord(word) {
  if (!word) return word;
  const lower = word.toLowerCase();
  let out = lower.charAt(0).toUpperCase() + lower.slice(1);

  // Mc prefix — mcdonald → McDonald
  if (out.length > 2 && out.startsWith('Mc')) {
    out = 'Mc' + out.charAt(2).toUpperCase() + out.slice(3);
  }
  // Mac prefix (long enough words only) — macgregor → MacGregor
  else if (out.length >= MAC_MIN_LEN && out.startsWith('Mac')) {
    out = 'Mac' + out.charAt(3).toUpperCase() + out.slice(4);
  }
  return out;
}

/**
 * Title-case a name-flavour string per the Batch B rules.
 * Returns the input unchanged when the user already typed any
 * uppercase letter, so their intent (McDonald, iPhone) is preserved.
 */
export function toTitleCase(input) {
  if (typeof input !== 'string') return input;
  const s = input;
  if (!s.trim()) return s;

  // Rule 1 — any uppercase already present? preserve intent.
  // v10.28.1 · Unicode-aware so Māori macron vowels (Ā/Ē/Ī/Ō/Ū) and
  // other non-ASCII capitals also short-circuit — critical for iwi
  // and hapū names in a Waikato CRM.
  if (/\p{Lu}/u.test(s)) return s;

  // Rule 2 — split on whitespace (keep whitespace tokens intact so
  // formatting round-trips cleanly), then split each word on the
  // apostrophe + hyphen boundaries, capitalising each sub-part.
  return s
    .split(/(\s+)/)
    .map((token) => {
      if (/^\s+$/.test(token) || token === '') return token;
      return token
        .split(/([-'])/)
        .map((part) => {
          if (part === '-' || part === "'") return part;
          return _capitaliseWord(part);
        })
        .join('');
    })
    .join('');
}

export default toTitleCase;
